import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import type { LocationFeatureDetails } from '../../../types/Location';
import { getFeatureSubDetail } from './featureSubDetails';

interface FeatureSubDetailProps {
  feature: string;
  details: LocationFeatureDetails | null | undefined;
}

const listReset = { listStyle: 'none', m: 0, p: 0 };

/** Read-only sub-detail line(s) under a feature label; renders nothing when the supplier left them blank. */
const FeatureSubDetail = ({ feature, details }: FeatureSubDetailProps) => {
  const detail = getFeatureSubDetail(feature, details);
  if (!detail) return null;

  switch (detail.kind) {
    case 'text':
      return (
        <Typography variant="body2" color="text.secondary" data-testid="feature-sub-detail">
          {detail.text}
        </Typography>
      );
    case 'chips':
      return (
        <Stack
          component="ul"
          direction="row"
          flexWrap="wrap"
          gap={0.5}
          aria-label={`${feature} details`}
          data-testid="feature-sub-detail"
          sx={{ ...listReset, mt: 0.5 }}
        >
          {detail.items.map((item) => (
            <Chip key={item} component="li" size="small" label={item} sx={{ height: 22, fontSize: '0.75rem' }} />
          ))}
        </Stack>
      );
    case 'groups':
      return (
        <Stack direction="row" flexWrap="wrap" columnGap={4} rowGap={1} data-testid="feature-sub-detail" sx={{ mt: 0.25 }}>
          {detail.groups.map((group) => (
            <Box key={group.label}>
              <Typography variant="body2">{group.label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {group.items.join(', ')}
              </Typography>
            </Box>
          ))}
        </Stack>
      );
    default:
      return null;
  }
};

export default FeatureSubDetail;
